import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  Validators
} from '@angular/forms';
import { SelectComponent, selectKeyValueType, TextType } from '..';
import { ClassName, LabelSelectHeight, SharedConstants } from '@app/eds';
import { IconSizeConfig } from '@app/eds/constants';

const CONTROL_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => LabelSelectComponent),
  multi: true
};

@Component({
  selector: 'app-label-select[labelText][labelTextType][selectData]',
  templateUrl: './label-select.component.html',
  styleUrls: ['./label-select.component.scss'],
  providers: [CONTROL_VALUE_ACCESSOR]
})
export class LabelSelectComponent implements OnInit, OnChanges, ControlValueAccessor {
  // provides content of label
  @Input() labelText!: string;

  // set selector state
  @Input() isDisabled = false;

  // provides label text type
  @Input() labelTextType!: TextType;

  // This field is to disable the component
  @Input() disabled = false;

  // Shows transparent select with border when its true
  @Input() isTransparentSelect = false;

  // provide height for the select component
  @Input() height: string = LabelSelectHeight;

  // This dataset will be used as options in select and it should be passed as [{ value: 'steak-0', name: 'Steak' }]
  @Input() selectData!: selectKeyValueType[];

  // This field tells whether the input component is reuired or not , should be pass while calling the component.
  @Input() isRequired = false;

  // It is a Input() field which tells whether a field is required or not
  @Input() isSelectRequired = false;

  // Event emitter when a dropdown value is selected
  @Output() selectionChangeHandler: EventEmitter<string> = new EventEmitter<string>();

  // default value  of dropdown
  @Input() defaultValue!: selectKeyValueType | null;

  // Placeholder for the select field
  @Input() placeholder!: string;

  // Set select icon
  @Input() showIcon = false;

  // to select the panel class
  @Input() singleSelectPanelClass: string = SharedConstants.EMPTY_STRING;

  // custom class for mat select
  @Input() public customMatSelectTextClass: string = SharedConstants.EMPTY_STRING;

  // FormControl field for the select
  @Input() set control(value: FormControl) {
    if (value !== this.dataControl) {
      this.dataControl = value;
    }
  }

  // Single select text class
  @Input() singleSelectTextClass: string = ClassName.SelectText;

  // Boolean to specify if the select dropdown is a filter dropdown.
  // Will be used for displaying the UI of the select component accordingly.
  @Input() isFilterSelect = false;

  // to show red line if custom error present
  @Input() hasCustomError = false;

  // custom error msg should be a translated string
  @Input() customErrorMsg!: string;

  // data control for form
  dataControl = new FormControl('', Validators.required);

  // Child component, used to access its method
  @ViewChild('sharedLabelSelect') selectComponent!: SelectComponent;

  // Boolean to specify if the tooltip needs to be displayed.
  @Input() showTooltipIcon = false;

  // String to store the text to be displayed
  // when hovered over the tooltip icon.
  @Input() toolTipText!: string;

  // To store the bottom padding of the label.
  @Input() labelPaddingBottom!: string;

  // If true then do not show any bottom border
  @Input() selectNoBorder = false;

  // If true then show a thinner bottom border
  @Input() isThinBorder = false;

  // To store the custom styles for the label.
  labelStyles: { [key: string]: string } = {};

  // To store the tooltip icon size.
  iconSize: string = IconSizeConfig.ICON_SMALL;

  // Property manage value of app select
  public selectedOption!: selectKeyValueType;

  private _value!: string;

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes) {
      return;
    }

    if (changes['defaultValue'] && this.defaultValue) {
      this.selectedOption = this.defaultValue;
    }
  }

  ngOnInit() {
    this.setLabelStyles();
  }

  public onchange = (value: string | number) => { };

  public onTouched = () => { };

  public get value(): string {
    return this._value;
  }

  public set value(v: string) {
    const selected = this.selectData.find((item) => item.name === v);
    if (!selected) {
      return;
    }

    this.selectedOption = selected;
    this._value = v;
    this.dataControl.setValue(v);
    this.onchange(v);
    this.onTouched();
    this.selectionChangeHandler.emit(v);
  }

  public writeValue(obj: string): void {
    this.value = obj;
  }

  public registerOnChange(fn: any): void {
    this.onchange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  /**
   * When new dropdown value is selected emit happens with the keyname passed as value
   *
   * @param {string} keyName is the keyname of selcted option
   */
  onSelectionChange = (keyName: string): void => {
    this.selectionChangeHandler.emit(keyName);
    this.dataControl.setValue(keyName);
  };

  /**
   * Updates the label select value shown in UI with
   * the provided value in the child select component.
   *
   * @param {selectKeyValueType} selectValue
   */
  updateSelectComponentValue = (selectValue: selectKeyValueType) => {
    this.selectComponent.dataControl.setValue(selectValue.value);
    this.selectComponent.selectedValue = selectValue.value;
  };

  /**
   * Sets the custom styles for the title/label for the select component.
   */
  setLabelStyles = () => (this.labelStyles = { paddingBottom: this.labelPaddingBottom });
}
